import { useState } from 'react';
import { countyGeo, countyStatusMap } from '../data/waCounties';
import { roadmap } from '../mock';

const SERIF = "'Fraunces', Georgia, serif";
const INK = '#3E5C76';
const NAVY = '#0B2545';

const tones = {
  live: { fill: '#A9D6C2', hover: '#8FC9AF', label: 'Live Now', swatch: '#6EB598', hatch: true },
  soon: { fill: '#B4CDE3', hover: '#9CBDDA', label: 'Coming 2026', swatch: '#8FB4D4' },
  later: { fill: '#C0C2E0', hover: '#ABAED6', label: 'Coming 2027', swatch: '#A3A6D1' },
  roadmap: { fill: '#EDF1F6', hover: '#E0E7EF', label: 'On Roadmap', swatch: '#CBD5E1' },
};

const statusMap = countyStatusMap(roadmap);
const CORNERS = [{ x: 14, y: 14 }, { x: 746, y: 14 }, { x: 14, y: 506 }, { x: 746, y: 506 }];

const VIEWBOX_WIDTH = 760;
const VIEWBOX_HEIGHT = 520;
const MAP_SCALE = 0.94;
const MAP_OFFSET_Y = 44;
const MAP_CENTER = { x: 380, y: 230 };
const COMPASS_SPOKE_ANGLES = [0, 45, 90, 135];
const SCALE_SEGMENT_WIDTH = 26;
const TOOLTIP_MIN_X = 95;
const TOOLTIP_MAX_X = 665;
const TOOLTIP_MIN_Y = 84;
const TOOLTIP_LIFT_Y = 34;

const projectX = (x) => MAP_CENTER.x + (x - MAP_CENTER.x) * MAP_SCALE;
const projectY = (y) => MAP_OFFSET_Y + MAP_CENTER.y + (y - MAP_CENTER.y) * MAP_SCALE;

const countyFill = (tone, highlighted) => {
  if (tone === 'live') return highlighted ? 'url(#liveHatchHover)' : 'url(#liveHatch)';
  return highlighted ? tones[tone].hover : tones[tone].fill;
};

const MapDefs = () => (
  <defs>
    <filter id="paperGrain">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" result="noise" />
      <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0.24 0 0 0 0 0.36 0 0 0 0 0.46 0 0 0 0.04 0" />
      <feComposite operator="over" in2="SourceGraphic" />
    </filter>
    <radialGradient id="parchment" cx="50%" cy="42%" r="78%">
      <stop offset="0%" stopColor="#FDFEFF" />
      <stop offset="65%" stopColor="#F3F6FA" />
      <stop offset="100%" stopColor="#E3EAF2" />
    </radialGradient>
    <pattern id="liveHatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
      <rect width="6" height="6" fill="#A9D6C2" />
      <line x1="0" y1="0" x2="0" y2="6" stroke="#79BB9F" strokeWidth="1.4" />
    </pattern>
    <pattern id="liveHatchHover" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
      <rect width="6" height="6" fill="#8FC9AF" />
      <line x1="0" y1="0" x2="0" y2="6" stroke="#63AC8C" strokeWidth="1.4" />
    </pattern>
  </defs>
);

const PaperFrame = () => (
  <g>
    <rect x="4" y="4" width="752" height="512" rx="3" fill="url(#parchment)" stroke="#C3D0DE" strokeWidth="1" />
    <rect x="4" y="4" width="752" height="512" rx="3" fill="transparent" filter="url(#paperGrain)" opacity="0.6" style={{ pointerEvents: 'none' }} />
    <rect x="14" y="14" width="732" height="492" fill="none" stroke={INK} strokeWidth="1.6" opacity="0.75" />
    <rect x="19" y="19" width="722" height="482" fill="none" stroke={INK} strokeWidth="0.5" opacity="0.6" />
    {CORNERS.map((c) => (
      <circle key={`corner-${c.x}-${c.y}`} cx={c.x} cy={c.y} r="2.2" fill={INK} opacity="0.75" />
    ))}
  </g>
);

const Cartouche = () => (
  <g transform="translate(380, 48)" style={{ pointerEvents: 'none' }}>
    <text textAnchor="middle" fontFamily={SERIF} fontSize="19" letterSpacing="6" fill={NAVY} fontWeight="600">
      WASHINGTON
    </text>
    <text textAnchor="middle" y="17" fontFamily={SERIF} fontSize="9.5" letterSpacing="3" fill={INK} opacity="0.8" fontStyle="italic">
      County Survey of Title Coverage
    </text>
    <line x1="-130" y1="26" x2="130" y2="26" stroke={INK} strokeWidth="0.6" opacity="0.6" />
    <circle cx="0" cy="26" r="1.6" fill={INK} opacity="0.7" />
  </g>
);

const CompassRose = () => (
  <g transform="translate(688, 122)" opacity="0.85" style={{ pointerEvents: 'none' }}>
    <circle r="22" fill="none" stroke={INK} strokeWidth="0.7" />
    <circle r="17" fill="none" stroke={INK} strokeWidth="0.4" />
    {COMPASS_SPOKE_ANGLES.map((a) => (
      <line key={`spoke-${a}`} x1="0" y1="-21" x2="0" y2="21" stroke={INK} strokeWidth="0.35" transform={`rotate(${a})`} />
    ))}
    <path d="M0 -20 L4 0 L0 6 L-4 0 Z" fill={NAVY} />
    <path d="M0 20 L3 2 L0 -2 L-3 2 Z" fill="#FFFFFF" stroke={INK} strokeWidth="0.6" />
    <text y="-27" textAnchor="middle" fontFamily={SERIF} fontSize="11" fontWeight="600" fill={INK}>N</text>
  </g>
);

const ScaleBar = () => (
  <g transform="translate(56, 480)" opacity="0.8" style={{ pointerEvents: 'none' }}>
    <text fontFamily={SERIF} fontSize="8.5" fontStyle="italic" fill={INK} y="-6">Scale of Miles</text>
    {[0, 1, 2, 3].map((i) => (
      <rect key={`mile-${i}`} x={i * SCALE_SEGMENT_WIDTH} y="0" width={SCALE_SEGMENT_WIDTH} height="4.5" fill={i % 2 === 0 ? INK : '#FFFFFF'} stroke={INK} strokeWidth="0.5" />
    ))}
    <text fontFamily={SERIF} fontSize="7.5" fill={INK} y="14" x="0">0</text>
    <text fontFamily={SERIF} fontSize="7.5" fill={INK} y="14" x="98">100</text>
  </g>
);

const strokeWidthFor = (selected, highlighted) => {
  if (selected) return 1.8;
  if (highlighted) return 1.2;
  return 0.7;
};

const CountyShape = ({ name, geo, tone, highlighted, selected, dimmed, onHover, onSelect }) => (
  <path
    d={geo.d}
    fill={countyFill(tone, highlighted || selected)}
    fillRule="evenodd"
    stroke={selected ? NAVY : INK}
    strokeWidth={strokeWidthFor(selected, highlighted)}
    opacity={dimmed ? 0.35 : 1}
    onMouseEnter={() => onHover(name)}
    onMouseLeave={() => onHover(null)}
    onClick={() => onSelect(name)}
    style={{ cursor: 'pointer', transition: 'opacity .2s ease' }}
    data-testid={`county-${name.replace(/\s+/g, '-').toLowerCase()}`}
  />
);

const MapTooltip = ({ name, geo, toneLabel }) => (
  <g
    transform={`translate(${Math.min(Math.max(projectX(geo.cx), TOOLTIP_MIN_X), TOOLTIP_MAX_X)}, ${Math.max(projectY(geo.cy) - TOOLTIP_LIFT_Y, TOOLTIP_MIN_Y)})`}
    style={{ pointerEvents: 'none' }}
  >
    <rect x="-88" y="-30" width="176" height="40" fill="#FFFFFF" stroke={INK} strokeWidth="1" />
    <rect x="-84" y="-26" width="168" height="32" fill="none" stroke={INK} strokeWidth="0.4" opacity="0.6" />
    <text textAnchor="middle" y="-14" fontFamily={SERIF} fontSize="12.5" fontWeight="600" fill={NAVY}>
      {name}
    </text>
    <text textAnchor="middle" y="1" fontFamily={SERIF} fontSize="9" fontStyle="italic" fill={INK}>
      {toneLabel}
    </text>
  </g>
);

const MapLegend = () => (
  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 justify-center" data-testid="map-legend">
    {Object.entries(tones).map(([k, v]) => (
      <div key={k} className="inline-flex items-center gap-2 text-[12px] text-slate-600" style={{ fontFamily: SERIF }}>
        <span
          className="inline-block w-4 h-3 border"
          style={{
            background: v.hatch
              ? `repeating-linear-gradient(45deg, ${v.swatch}, ${v.swatch} 2px, ${v.fill} 2px, ${v.fill} 4px)`
              : v.fill,
            borderColor: INK,
          }}
        />
        {v.label}
      </div>
    ))}
  </div>
);

const CountyLayer = ({ activeTone, hovered, selectedCounty, onHover, onSelect }) => (
  <>
    {Object.entries(countyGeo).map(([name, geo]) => {
      const tone = statusMap[name] || 'roadmap';
      const highlighted = hovered === name;
      const selected = selectedCounty === name;
      const inTone = !activeTone || activeTone === tone;
      return (
        <CountyShape
          key={name}
          name={name}
          geo={geo}
          tone={tone}
          highlighted={highlighted}
          selected={selected}
          dimmed={!inTone && !highlighted && !selected}
          onHover={onHover}
          onSelect={onSelect}
        />
      );
    })}
  </>
);

const LiveLabels = () => (
  <>
    {roadmap[0].counties.map((name) => {
      const geo = countyGeo[name];
      if (!geo) return null;
      return (
        <text
          key={name}
          x={geo.cx}
          y={geo.cy}
          textAnchor="middle"
          fontFamily={SERIF}
          fontSize="8.5"
          fontStyle="italic"
          fill="#1E5B44"
          style={{ pointerEvents: 'none' }}
        >
          {name.replace(' County', '')}
        </text>
      );
    })}
  </>
);

const WAMap = ({ activeTone, onSelectCounty, selectedCounty }) => {
  const [hovered, setHovered] = useState(null);
  const focus = hovered || selectedCounty;
  const handleSelect = (name) => onSelectCounty && onSelectCounty(name);

  return (
    <div className="relative w-full" data-testid="wa-county-map">
      <svg viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`} className="w-full h-auto" role="img" aria-label="Washington State county roadmap map">
        <MapDefs />
        <PaperFrame />
        <Cartouche />
        <g transform={`translate(0, ${MAP_OFFSET_Y}) scale(${MAP_SCALE})`} style={{ transformOrigin: `${MAP_CENTER.x}px ${MAP_CENTER.y}px` }}>
          <CountyLayer
            activeTone={activeTone}
            hovered={hovered}
            selectedCounty={selectedCounty}
            onHover={setHovered}
            onSelect={handleSelect}
          />
          <LiveLabels />
        </g>
        {focus && countyGeo[focus] && (
          <MapTooltip name={focus} geo={countyGeo[focus]} toneLabel={tones[statusMap[focus] || 'roadmap'].label} />
        )}
        <CompassRose />
        <ScaleBar />
        <text x="704" y="492" textAnchor="end" fontFamily={SERIF} fontSize="8.5" fontStyle="italic" fill={INK} opacity="0.75" style={{ pointerEvents: 'none' }}>
          N.W. Title Alliance — Assessor's Series, Plate No. 39
        </text>
      </svg>
      <MapLegend />
    </div>
  );
};

export default WAMap;
